import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from '@/shared/hooks/useLocation';
import { useToast } from '@/shared/ui/Toast';
import { reviewsApi } from '../api/reviewsApi';
import { reviewKeys } from '../queryKeys';

// Lets the worker pick up and start processing the sync job before we refetch.
const SYNC_INVALIDATE_DELAY_MS = 2000;
// Longer delay: bulk regeneration queues multiple jobs; give workers time to start drafting.
const REGENERATE_ALL_INVALIDATE_DELAY_MS = 5000;

export function useReviewActions() {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  const queryClient = useQueryClient();
  const toast = useToast();

  // Prefix match invalidates both the "all" and "not responded" lists + stats.
  const invalidateLists = () => {
    queryClient.invalidateQueries({ queryKey: reviewKeys.list(locationId) });
    queryClient.invalidateQueries({ queryKey: reviewKeys.stats(locationId) });
  };

  const post = useMutation({
    mutationFn: ({ id, editedText }: { id: string; editedText?: string }) =>
      reviewsApi.post(id, { locationId, editedText }),
    onSuccess: () => {
      invalidateLists();
      toast.success('Reply posted to Google');
    },
    onError: (err: unknown) => toast.error((err as Error).message ?? 'Could not post reply'),
  });

  const regenerate = useMutation({
    mutationFn: (id: string) => reviewsApi.regenerate(id),
    onSuccess: () => invalidateLists(),
    onError: (err: unknown) => toast.error((err as Error).message ?? 'Could not regenerate draft'),
  });

  const skip = useMutation({
    mutationFn: (id: string) => reviewsApi.skip(id),
    onSuccess: () => {
      invalidateLists();
      toast.info('Review dismissed');
    },
    onError: (err: unknown) => toast.error((err as Error).message ?? 'Could not dismiss review'),
  });

  const sync = useMutation({
    mutationFn: () => reviewsApi.sync(locationId),
    onSuccess: () => {
      toast.success('Refreshing reviews from Google…');
      setTimeout(invalidateLists, SYNC_INVALIDATE_DELAY_MS);
    },
    onError: (err: unknown) => toast.error((err as Error).message ?? 'Could not refresh reviews'),
  });

  const regenerateAll = useMutation({
    mutationFn: () => reviewsApi.regenerateAll(locationId),
    onSuccess: () => {
      toast.success('Regenerating all drafts in the background…');
      setTimeout(invalidateLists, REGENERATE_ALL_INVALIDATE_DELAY_MS);
    },
    onError: (err: unknown) => toast.error((err as Error).message ?? 'Could not regenerate drafts'),
  });

  return { post, regenerate, skip, sync, regenerateAll };
}
